import React, { useState, useEffect, Fragment, useMemo } from "react"; // Added useMemo
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import Slider from "react-slick";
import Zoom from "react-medium-image-zoom";
import { Tab } from "@headlessui/react";
import {
  ChevronRight,
  ArrowRight,
  ChevronLeft,
  Star,
  CheckCircle,
  Palette,
  Ruler,
  Info, // Keep other icons if needed by summary data
} from "lucide-react";
import * as LucideIcons from "lucide-react"; // Import all icons for dynamic use

// --- Mock Data Import ---
// Make sure this path is correct and the data structure matches expectations
import { shadeTypeDetails } from "../data"; // Assuming this holds the structure you provided

import "react-medium-image-zoom/dist/styles.css";
import { useSelector } from "react-redux";

// Helper function for class names
function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

// --- Reusable Components ---

// Custom Arrow for Slider
const SlickArrow = ({ className, style, onClick, direction = "next" }) => (
  <button
    className={`${className} z-10 w-10 h-10 flex items-center justify-center bg-white/80 hover:bg-white rounded-full shadow-md text-primary transition-colors duration-200 before:content-none`}
    style={{ ...style, display: "flex" }} // Ensure display:flex
    onClick={onClick}
    aria-label={direction === "next" ? "Next slide" : "Previous slide"}
  >
    {direction === "next" ? (
      <ChevronRight className="w-6 h-6" />
    ) : (
      <ChevronLeft className="w-6 h-6" />
    )}
  </button>
);

// Section for displaying media (Image or Video) with Zoom
const MediaDisplay = ({ mediaUrl, altText }) => {
  if (!mediaUrl) return null; // Don't render if no media URL

  const isVideo = mediaUrl.toLowerCase().endsWith(".mp4");

  return (
    <div className="mb-6 overflow-hidden rounded-lg ">
      {" "}
      {/* Added bg for loading */}
      <Zoom
        overlayBgColorStart="rgba(0, 0, 0, 0)"
        overlayBgColorEnd="rgba(0, 0, 0, 0.9)"
        transitionDuration={300}
      >
        {isVideo ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-auto max-h-[500px] object-contain display-block"
            key={mediaUrl} // Key helps React identify changes
            // Add error handling if needed
            onError={(e) => console.error("Video load error:", mediaUrl, e)}
          >
            <source src={mediaUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={mediaUrl}
            alt={altText || "Product media"} // Provide default alt text
            className="rounded-lg w-full h-auto object-contain display-block"
            // Add error handling if needed
            onError={(e) => {
              e.target.style.display = "none";
              console.error("Image load error:", mediaUrl, e);
            }} // Hide broken images
          />
        )}
      </Zoom>
    </div>
  );
};

// --- Main Page Component ---
const ShadeInfoPage = () => {
  const params = useParams();
  const navigate = useNavigate();

  const paramId = params.id || "";
  const shadeTypeParam = paramId.split("&")[0].replace(/-/g, "_");
  const fabricTypeParam = paramId.includes("fabric=")
    ? paramId.split("fabric=")[1]?.split("&")[0]?.replaceAll("_", " ") || ""
    : "all"; // Default fabric if not specified

  const shadeData = shadeTypeDetails[shadeTypeParam];
  const country = useSelector((state) => state.country); // Ensure correct path in Redux state

  const [imgGallery, setImgGallery] = useState([]);

  // Define the order and potential keys for tabs
  const potentialTabKeys = useMemo(
    () => [
      "Control Options",
      "Valance Options",
      "Bottom Bar Options",
      "Fabric Options",
      // Add other keys like "Specifications", "Installation" if they might exist
    ],
    []
  );

  // Dynamically create tab sections based on available data in the desired order
  const tabSections = useMemo(
    () =>
      potentialTabKeys
        .map((key) => ({
          key: key,
          title: key, // Use the key as the title, or map to a nicer title if needed
        }))
        .filter(
          (section) =>
            shadeData &&
            Array.isArray(shadeData[section.key]) &&
            shadeData[section.key].length > 0
        ),
    [shadeData, potentialTabKeys]
  );

  // Load gallery images from shadeData.Gallery
  useEffect(() => {
    if (shadeData?.Gallery && Array.isArray(shadeData.Gallery)) {
      setImgGallery(
        shadeData.Gallery.map((url, index) => ({
          id: index + 1,
          imageUrl: url,
        }))
      );
    } else {
      setImgGallery([]); // Clear gallery if data is missing or not an array
    }
  }, [shadeData]);

  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: imgGallery.length > 2,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    centerMode: imgGallery.length > 2, // Center mode only if enough slides
    autoplay: true,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    nextArrow: <SlickArrow direction="next" />,
    prevArrow: <SlickArrow direction="prev" />,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2, centerMode: false } },
      { breakpoint: 768, settings: { slidesToShow: 1, centerMode: false } },
    ],
    appendDots: (dots) => (
      <div>
        <ul className="m-0 slick-dots-custom">{dots}</ul>
      </div>
    ),
    customPaging: (i) => (
      <button className="w-3 h-3 bg-white/50 rounded-full focus:outline-none transition-colors duration-200 slick-dot-custom"></button>
    ),
  };

  // Handle case where shade data doesn't exist
  if (!shadeData) {
    // Optional: Log the attempted shadeTypeParam for debugging
    console.error(`Shade data not found for type: ${shadeTypeParam}`);
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-center p-8">
        <div>
          <h1 className="text-3xl font-bold text-red-600 mb-4">
            Product Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            Sorry, we couldn't find details for this shade type (
            {shadeTypeParam}).
          </p>
          <Link
            to="/shop"
            className="inline-block px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
          >
            Back to Shop
          </Link>
        </div>
      </div>
    );
  }

  // Calculate Price
  const startingPrice = shadeData.hero?.price;
  const displayPrice = startingPrice
    ? country === "USA"
      ? (startingPrice * 0.75).toFixed(2)
      : startingPrice.toFixed(2)
    : null;
  const currency = country === "USA" ? "USD" : "CAD";

  // Construct customize link using data if available, otherwise generate
  const customizeLink =
    shadeData.actions?.[0]?.to ||
    `/customize/details?default=${shadeTypeParam}&fabric=${fabricTypeParam}`;

  // --- Render ---
  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <Helmet>
        <title>
          {shadeData.metaTitle ||
            `${shadeData.hero?.header || "Product Details"} | Off Cut Shades`}
        </title>
        <meta
          name="description"
          content={
            shadeData.metaDesc ||
            shadeData.hero?.body ||
            `Learn more about ${shadeData.hero?.header || "our shades"}.`
          }
        />
      </Helmet>

      {/* 1. Hero Section */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 bg-white overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 md:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="space-y-6 text-center lg:text-left"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                {shadeData.hero?.header || "Product Details"}
              </h1>

              <p className="text-lg md:text-xl text-gray-600 max-w-xl mx-auto lg:mx-0">
                {shadeData.hero?.body}
              </p>

              {/* Key Benefits (Optional - Add to data if needed) */}
              {shadeData.keyBenefits && (
                <div className="flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2 text-sm text-gray-700">
                  {shadeData.keyBenefits.map((benefit, index) => (
                    <span key={index} className="flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4 text-green-600" />{" "}
                      {benefit}
                    </span>
                  ))}
                </div>
              )}

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate(customizeLink)}
                  className="px-7 py-3 bg-primary text-white rounded-lg font-semibold text-lg shadow-md hover:bg-primary/90 transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  {shadeData.actions?.[0]?.text || "Customize Now"}{" "}
                  {/* Use text from data or default */}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/swatches")}
                  className="px-7 py-3 border-2 border-secondary text-secondary rounded-lg font-semibold text-lg hover:bg-secondary/10 transition-all duration-200 flex items-center justify-center gap-2 group"
                >
                  Get Free Samples <Palette className="w-5 h-5 opacity-80" />
                </motion.button>
              </div>
            </motion.div>

            {/* Right Image & Price */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.1, ease: "easeOut" }}
              className="relative mt-8 lg:mt-0" // Added margin top for mobile
            >
              {shadeData.hero?.img && (
                <img
                  src={shadeData.hero.img}
                  alt={shadeData.hero.header || "Product image"}
                  // Adjust aspect ratio
                />
              )}
              {displayPrice && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="absolute -bottom-4 -right-4 md:bottom-4 md:right-4 bg-white p-4 rounded-lg shadow-lg border border-gray-100 text-center"
                >
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                    Starting From
                  </div>
                  <div className="mt-0.5 flex flex-col">
                    <div className="text-2xl font-bold text-primary">
                      ${(displayPrice * 0.5 * 0.8).toFixed(2)}
                      <span className="text-sm font-medium ml-1">
                        {currency}
                      </span>
                    </div>
                    <span className="text-gray-500 line-through text-xs font-medium">
                      ${displayPrice}
                      <span className="text-xs">{currency}</span>
                    </span>
                    <span className="text-xs font-semibold bg-red-100 text-red-600 px-2 py-0.5 rounded mt-1">
                      50% + 20%
                    </span>
                  </div>
                </motion.div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. "At a Glance" / Key Features Summary (Optional - Add 'summary' to data) */}
      {shadeData.summary &&
        Array.isArray(shadeData.summary) &&
        shadeData.summary.length > 0 && (
          <section className="py-12 md:py-16 bg-primary/5">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-8">
                {shadeData.hero?.header} at a Glance
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                {shadeData.summary.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4 p-4"
                  >
                    <div className="mt-1 flex-shrink-0 text-primary">
                      {item.icon && LucideIcons[item.icon] ? (
                        React.createElement(LucideIcons[item.icon], {
                          className: "w-7 h-7",
                        })
                      ) : (
                        <Star className="w-7 h-7" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-1">
                        {item.title}
                      </h3>
                      <p className="text-gray-600 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

      {/* 3. Main Content Tabs */}
      {tabSections.length > 0 && (
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <Tab.Group>
              {/* Tab Navigation */}
              <Tab.List className="flex flex-wrap justify-center border-b border-gray-200 mb-10 md:mb-12 -mx-2">
                {tabSections.map((section) => (
                  <Tab key={section.key} as={Fragment}>
                    {({ selected }) => (
                      <button
                        className={classNames(
                          "px-4 sm:px-6 py-3 text-sm sm:text-base font-medium leading-5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary/50 mx-1 sm:mx-2 mb-[-1px]",
                          selected
                            ? "border-b-2 border-primary text-primary"
                            : "border-b-2 border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        )}
                      >
                        {section.title}
                      </button>
                    )}
                  </Tab>
                ))}
              </Tab.List>

              {/* Tab Panels (Content) */}
              <Tab.Panels>
                {tabSections.map((section) => (
                  <Tab.Panel key={section.key} className="focus:outline-none">
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={section.key}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -15 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-10 md:space-y-14"
                      >
                        {/* Iterate over subsections within the current tab's data */}
                        {shadeData[section.key]?.map((subsection, index) => (
                          <div
                            key={`${section.key}-${index}`}
                            className="grid lg:grid-cols-2 gap-8 md:gap-12 items-start"
                          >
                            {" "}
                            <div
                              className={`order-1 ${
                                index % 2 === 0 ? "lg:order-2" : "lg:order-1"
                              }`}
                            >
                              {subsection.media &&
                                subsection.media.length > 0 && (
                                  <MediaDisplay
                                    mediaUrl={subsection.media[0]}
                                    altText={subsection.title}
                                  />
                                )}
                            </div>
                            {/* Content Side (Text) */}
                            <div
                              className={`order-2 ${
                                index % 2 === 0 ? "lg:order-1" : "lg:order-2"
                              } lg:pt-2`}
                            >
                              <h3 className="text-xl md:text-2xl font-semibold text-gray-900 mb-3 md:mb-4">
                                {subsection.title}
                              </h3>
                              {/* Render body content, handling arrays and JSX */}
                              <div className="prose prose-gray max-w-none text-gray-600 space-y-4">
                                {Array.isArray(subsection.body) ? (
                                  subsection.body.map((content, pIndex) => (
                                    // Directly render strings or JSX elements
                                    <div key={pIndex}>{content}</div>
                                  ))
                                ) : typeof subsection.body === "string" ? (
                                  <p>{subsection.body}</p> // Wrap string in <p>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    </AnimatePresence>
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>
        </section>
      )}

      {/* 4. Related Actions / Next Steps */}
      <section className="py-12 md:py-16 bg-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {/* Customize */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col">
              <h3 className="text-lg font-semibold mb-3">Ready to Style?</h3>
              <p className="text-sm text-gray-600 mb-4 flex-grow">
                Start designing your perfect{" "}
                {shadeData.hero?.header || "shades"} now.
              </p>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate(customizeLink)}
                className="w-full mt-auto px-5 py-2.5 bg-primary text-white rounded-lg font-medium text-center shadow-sm hover:bg-primary/90 transition-colors duration-200 flex items-center justify-center gap-2 group"
              >
                {shadeData.actions?.[0]?.text || "Customize Now"}{" "}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </div>
            {/* Get Samples */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex flex-col">
              <h3 className="text-lg font-semibold mb-3">See the Fabrics</h3>
              <p className="text-sm text-gray-600 mb-4 flex-grow">
                Order free fabric samples to find the perfect match for your
                room.
              </p>
              <Link
                to="/swatches"
                className="w-full mt-auto inline-block px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg font-medium text-center hover:border-gray-400 hover:bg-gray-50 transition-colors duration-200"
              >
                Get Free Samples
              </Link>
            </div>
            {/* Measure Guide */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 sm:col-span-2 lg:col-span-1 flex flex-col">
              <h3 className="text-lg font-semibold mb-3">
                Measure with Confidence
              </h3>
              <p className="text-sm text-gray-600 mb-4 flex-grow">
                Our easy guide helps you get the perfect measurements every
                time.
              </p>
              <Link
                to="/guides/measuring-guide"
                className="w-full mt-auto inline-block px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg font-medium text-center hover:border-gray-400 hover:bg-gray-50 transition-colors duration-200"
              >
                How to Measure
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Gallery Section */}
      {imgGallery.length > 0 && (
        <section className="py-16 md:py-24 bg-gradient-to-b from-gray-800 to-black text-white overflow-hidden">
          <div className="container mx-auto px-0 md:px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-10 md:mb-12 px-4">
              Get Inspired by {shadeData.hero?.header || "These Styles"}
            </h2>
            <Slider {...sliderSettings} className="-mx-2 md:mx-0">
              {imgGallery.map((item) => (
                <div key={item.id} className="px-2 md:px-3">
                  <motion.div
                    className="overflow-hidden rounded-lg shadow-lg"
                    whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  >
                    <Zoom
                      overlayBgColorStart="rgba(0,0,0,0)"
                      overlayBgColorEnd="rgba(0,0,0,0.9)"
                    >
                      <img
                        src={item.imageUrl}
                        alt={`Inspiration gallery image ${item.id}`}
                        className="w-full aspect-[4/3] object-cover display-block"
                      />
                    </Zoom>
                  </motion.div>
                </div>
              ))}
            </Slider>
          </div>
        </section>
      )}
    </div> // End of outermost div
  );
};

export default ShadeInfoPage;
